import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Locale } from '@durma-soft/gros-sdk';
import { RestaurantWeekMenus } from '@durma-soft/gros-sdk/dist/types/restaurants';

import { Alert } from '@/components/shared/alert';
import { Skeleton } from '@/components/shared/shadcn-ui/skeleton';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shared/shadcn-ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@/components/shared/shadcn-ui/table';

import { repeat } from '@/utils/helpers';
import { locales } from '@/config/global/languages';

interface CopyRestaurantWeekMenuPreviewProps {
  hasSourceWeek: boolean;
  data?: RestaurantWeekMenus;
  isLoading: boolean;
  isError: boolean;
}

export const CopyRestaurantWeekMenuPreview = ({
  hasSourceWeek,
  data,
  isLoading,
  isError,
}: CopyRestaurantWeekMenuPreviewProps) => {
  const { t, i18n } = useTranslation();
  const locale = locales[i18n.language as Locale];

  if (!hasSourceWeek) {
    return (
      <p className="text-center text-muted-foreground">
        {t('menu.copy.choose-source-week')}
      </p>
    );
  }

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 gap-5 md:grid-cols-3 xl:grid-cols-4">
        {repeat(8).map((_, index) => (
          <Skeleton key={index} className="h-40 w-full" />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <Alert
        title={t('common.error')}
        description={t('menu.error-loading')}
        variant="destructive"
        className="mx-auto"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {Object.entries(data).map(([date, shifts]) => {
        // T00:00:00 forces local-time parsing so the weekday never shifts across timezones.
        const dayLabel = format(new Date(`${date}T00:00:00`), 'EEEE, dd.MM.', {
          locale,
        });
        const shiftEntries = Object.entries(shifts);
        return (
          <div key={date} className="flex flex-col gap-3">
            <h3 className="font-semibold capitalize md:text-lg">{dayLabel}</h3>
            {shiftEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {t('menu.copy.no-menus-for-day')}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {shiftEntries.flatMap(([shift, menus]) =>
                  menus.map((menu) => (
                    <Card
                      key={menu.id}
                      className="flex w-full flex-col rounded-md"
                    >
                      <CardHeader className="flex-shrink-0 rounded-tl-md rounded-tr-md bg-accent p-3">
                        <CardTitle
                          title={menu.menu_type.name}
                          className="truncate text-base text-accent-foreground"
                        >
                          {menu.menu_type.name} — {shift}.{' '}
                          {t('menu.shift').toLowerCase()}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1 rounded-bl-md rounded-br-md bg-card p-0">
                        <Table>
                          <TableBody>
                            {menu.foods.map((food) => (
                              <TableRow key={food.id}>
                                <TableCell>{food.name}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  )),
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
